import { getLocaleInfo, Locales, LOCALES } from '../locales'

export type TranslateFunction = (
  key: string,
  values?: Record<string, unknown>,
  options?: { flat: boolean },
) => string

export interface I18n {
  locale: string
  locales: Locales
  localeInfo: {
    currency: string
    distanceUnit: string
  }
  current: {
    currency: string
  }
  $t: TranslateFunction
}

declare module 'nuxt/app' {
  interface NuxtApp {
    $t: TranslateFunction
    $i18n: I18n
  }
}

export default defineNuxtPlugin(async (nuxtApp) => {
  nuxtApp.vueApp.directive('thtml', {
    mounted: (el, binding) => {
      el.innerHTML = binding.value
    },
    updated: (el, binding) => {
      if (binding.value !== binding.oldValue) {
        el.innerHTML = binding.value
      }
    },
    getSSRProps(binding) {
      return {
        innerHTML: binding.value,
      }
    },
  })
  const route = useRoute()
  const routeLang = route.params.lang as string
  const localeInfo = getLocaleInfo(routeLang)
  let localeFile = await importLocaleFile(localeInfo.fileName)
  if (!localeFile) {
    localeFile = await importLocaleFile('en')
  }
  localeFile = localeFile.default
  const t2: TranslateFunction = (key, values?, options?) => {
    if (localeFile && localeFile[key]) {
      const translationResult = localeFile[key](values)
      const flatOption = options?.flat ?? true
      const result =
        flatOption && Array.isArray(translationResult)
          ? translationResult.flat().join('')
          : translationResult
      return result
    }
    return key
  }
  nuxtApp.provide('t', t2)

  const i18n = reactive({
    locale: localeInfo.code,
    localeInfo,
    locales: LOCALES,
    $t: t2,
  })
  nuxtApp.provide('i18n', i18n)
})

async function retryImport(
  fn: () => Promise<any>,
  retries = 3,
  delay = 1000,
): Promise<any> {
  try {
    return await fn()
  } catch (err) {
    if (retries === 0) {
      throw err
    }
    await new Promise((resolve) => setTimeout(resolve, delay))
    return retryImport(fn, retries - 1, delay)
  }
}

function importLocaleFile(fileName: string) {
  return retryImport(() => import(`../compiled/${fileName}.js`))
}
